import { useState, useCallback, useMemo } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import { ChevronLeft, ChevronDown, ChevronUp, Copy, Check, Loader2, Pencil, X, Terminal } from "lucide-react";
import { trpcClient } from "@/lib/trpc";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { cn, extractErrorMessage } from "@/lib/utils";
import type { FeedbackStatus } from "@/shared/schemas/feedback.schema";
import { FEEDBACK_SOURCE_LABELS } from "@/shared/schemas/feedback.schema";
import {
  FEEDBACK_MAX_IMAGES,
  getAllowedFeedbackTransitions,
  isFeedbackAdmin,
  canProcessFeedback,
  type FeedbackTransitionRule,
} from "@/shared/schemas/feedbackStatus";
import { FeedbackStateTransitionDialog } from "@/components/feedback/FeedbackStateTransitionDialog";
import {
  FeedbackImageLightbox,
  FeedbackImagePickerButtons,
  FeedbackPriorityChip,
  FeedbackStatusChip,
  FeedbackThumbGrid,
  FeedbackTypeChip,
  FeedbackSourceChip,
  FeedbackOccurrenceChip,
  formatFeedbackDate,
  parseFeedbackImages,
  uploadFeedbackImages,
} from "@/components/feedback/feedback-ui";
import { FeedbackCommentsTimeline } from "./components/FeedbackCommentsTimeline";
import {
  FeedbackResolutionResult,
  formatEngineerDisplayName,
  formatReporterDisplayName,
} from "./components/TechnicalResolutionCard";
import { CreateFeedbackDialog } from "./components/CreateFeedbackDialog";
import { FeedbackEventTimeline } from "./components/FeedbackEventTimeline";
import { useAuthStore } from "@/stores/auth.store";
import { useConfirm } from "@/providers/ConfirmProvider";
import { FeedbackRichText } from "@/components/feedback/FeedbackRichText";

const ACTION_BUTTON_STYLES: Record<FeedbackTransitionRule["kind"], string> = {
  start: "bg-amber-500 hover:bg-amber-600 text-white border-amber-500",
  resolve: "bg-emerald-600 hover:bg-emerald-700 text-white border-emerald-600",
  reopen: "bg-white hover:bg-slate-50 text-slate-800 border-slate-300",
  close: "bg-slate-700 hover:bg-slate-800 text-white border-slate-700",
};

export default function FeedbackView({
  feedbackId,
  variant = "page",
  onClose,
}: {
  feedbackId: string;
  /** "modal": hiển thị trong popup trên danh sách (FeedbackDetailModal) */
  variant?: "page" | "modal";
  onClose?: () => void;
}) {
  const isModal = variant === "modal";
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const confirm = useConfirm();
  const currentUser = useAuthStore((s) => s.user);

  const [zoomImage, setZoomImage] = useState<string | null>(null);
  const [transitionDialogOpen, setTransitionDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [targetStatus, setTargetStatus] = useState<FeedbackStatus | null>(null);
  const [isUploadingBefore, setIsUploadingBefore] = useState(false);
  const [diagExpanded, setDiagExpanded] = useState(false);
  const [diagTab, setDiagTab] = useState<"console" | "network" | "breadcrumbs" | "env">("console");
  const [copiedDiag, setCopiedDiag] = useState(false);

  const { data: feedback, isLoading, error } = useQuery({
    queryKey: ["feedback", feedbackId],
    queryFn: async () => trpcClient.feedback.get.query({ id: feedbackId }),
  });

  // Cùng quy tắc với server (feedback.update): người tạo hoặc admin
  const canEditFeedback =
    !!currentUser &&
    (isFeedbackAdmin(currentUser as any) ||
      (!!feedback?.reportedBy &&
        (feedback.reportedBy === currentUser.id || feedback.reportedBy === currentUser.email)));

  const allowedTransitions = useMemo(
    () => getAllowedFeedbackTransitions(feedback?.status, currentUser as any),
    [feedback?.status, currentUser],
  );

  const updateStatusMutation = useMutation({
    mutationFn: async (vars: { id: string; status: FeedbackStatus; note?: string; images?: string[] }) =>
      trpcClient.feedback.updateStatus.mutate(vars),
    onSuccess: () => {
      toast.success("Đã cập nhật trạng thái");
      queryClient.invalidateQueries({ queryKey: ["feedback", feedbackId] });
      queryClient.invalidateQueries({ queryKey: ["feedbacks"] });
    },
    onError: (err: any) => {
      toast.error("Không thể chuyển trạng thái: " + extractErrorMessage(err));
    },
  });

  const updateImagesMutation = useMutation({
    mutationFn: async (vars: { id: string; images: string[]; screenshotBefore: string | null }) =>
      trpcClient.feedback.update.mutate(vars as any),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["feedback", feedbackId] });
      queryClient.invalidateQueries({ queryKey: ["feedbacks"] });
    },
    onError: (err: any) => {
      toast.error("Không thể cập nhật ảnh: " + extractErrorMessage(err));
    },
  });

  const beforeImages = useMemo(
    () =>
      Array.from(
        new Set(
          [
            ...(feedback?.screenshotBefore ? [feedback.screenshotBefore] : []),
            ...parseFeedbackImages(feedback?.images),
          ].filter(Boolean),
        ),
      ) as string[],
    [feedback?.screenshotBefore, feedback?.images],
  );

  const comments: any[] = (feedback as any)?.comments || [];
  const lastResolveEvent = useMemo(
    () => [...comments].reverse().find((c) => c.newStatus === "RESOLVED"),
    [comments],
  );
  const afterImages = useMemo(() => {
    const fromEvent = parseFeedbackImages(lastResolveEvent?.images);
    if (fromEvent.length) return fromEvent;
    return feedback?.screenshotAfter ? [feedback.screenshotAfter] : [];
  }, [lastResolveEvent, feedback?.screenshotAfter]);

  const handleAddBefore = useCallback(
    async (files: File[]) => {
      if (!feedback) return;
      setIsUploadingBefore(true);
      try {
        const urls = await uploadFeedbackImages(files, FEEDBACK_MAX_IMAGES - beforeImages.length, "Ảnh");
        if (urls.length) {
          const next = Array.from(new Set([...beforeImages, ...urls])).slice(0, FEEDBACK_MAX_IMAGES);
          await updateImagesMutation.mutateAsync({ id: feedback.id, images: next, screenshotBefore: next[0] || null });
          toast.success("Đã thêm ảnh");
        }
      } finally {
        setIsUploadingBefore(false);
      }
    },
    [feedback, beforeImages, updateImagesMutation],
  );

  const handleRemoveBefore = useCallback(
    async (idx: number) => {
      if (!feedback) return;
      if (!(await confirm("Xóa ảnh này khỏi phiếu?"))) return;
      const next = beforeImages.filter((_, i) => i !== idx);
      await updateImagesMutation.mutateAsync({ id: feedback.id, images: next, screenshotBefore: next[0] || null });
      toast.success("Đã xóa ảnh");
    },
    [feedback, beforeImages, confirm, updateImagesMutation],
  );

  const openTransition = (rule: FeedbackTransitionRule) => {
    setTargetStatus(rule.to);
    setTransitionDialogOpen(true);
  };

  const handleConfirmTransition = async ({ note, images }: { note: string; images: string[] }) => {
    if (!feedback || !targetStatus) return;
    await updateStatusMutation.mutateAsync({ id: feedback.id, status: targetStatus, note, images });
  };

  if (isLoading) {
    return <div className="p-8 text-center text-slate-500">Đang tải...</div>;
  }
  if (!feedback || error) {
    return (
      <div className="p-8 text-center text-slate-500 space-y-3">
        <p>Không tìm thấy phiếu phản hồi</p>
        {isModal && onClose ? (
          <button type="button" onClick={onClose} className="text-blue-600 underline">Đóng</button>
        ) : (
          <Link to="/feedback" className="text-blue-600 underline">Về danh sách</Link>
        )}
      </div>
    );
  }

  const reporterName = formatReporterDisplayName((feedback as any).reportedByName, feedback.reportedBy);
  const assigneeName = feedback.assignedTo
    ? formatEngineerDisplayName((feedback as any).assignedToName || feedback.assignedTo)
    : "—";
  const isResolvedOrClosed = feedback.status === "RESOLVED" || feedback.status === "CLOSED";
  const hasActions = allowedTransitions.length > 0;

  const actionButtons = (layout: "bar" | "stack") =>
    allowedTransitions.map((rule) => (
      <button
        key={`${rule.from}-${rule.to}`}
        type="button"
        data-testid={`fb-action-${rule.kind}`}
        onClick={() => openTransition(rule)}
        className={cn(
          "min-h-11 px-4 rounded-lg border text-sm font-semibold transition-colors cursor-pointer",
          layout === "bar" ? "flex-1 min-w-0" : "w-full",
          ACTION_BUTTON_STYLES[rule.kind],
        )}
      >
        {rule.actionLabel}
      </button>
    ));

  const infoRows: { label: string; value: React.ReactNode }[] = [
    { label: "Mã phiếu", value: <span className="font-semibold">{feedback.code}</span> },
    { label: "Trạng thái", value: <FeedbackStatusChip status={feedback.status} /> },
    { label: "Loại", value: <FeedbackTypeChip type={feedback.type} /> },
    { label: "Mức độ", value: <FeedbackPriorityChip priority={feedback.priority} /> },
    {
      label: "Nguồn",
      value: FEEDBACK_SOURCE_LABELS[(feedback as any).source as keyof typeof FEEDBACK_SOURCE_LABELS] || "Người dùng",
    },
    ...(Number((feedback as any).occurrenceCount || 1) > 1 || ((feedback as any).source && (feedback as any).source !== "USER")
      ? [
          {
            label: "Tần suất",
            value: (
              <span className="font-medium tabular-nums">
                ×{(feedback as any).occurrenceCount || 1} lần · {(feedback as any).affectedUsers || 1} người
              </span>
            ),
          },
          { label: "Lần đầu", value: formatFeedbackDate((feedback as any).firstSeenAt || feedback.createdAt) },
          { label: "Lần cuối", value: formatFeedbackDate((feedback as any).lastSeenAt || feedback.updatedAt) },
        ]
      : []),
    { label: "Module", value: feedback.module || "—" },
    {
      label: "Trang",
      value: feedback.pageUrl ? (
        <a href={feedback.pageUrl} className="text-blue-600 hover:underline break-all">{feedback.pageUrl}</a>
      ) : (
        "—"
      ),
    },
    ...((feedback as any).appVersion ? [{ label: "Phiên bản", value: (feedback as any).appVersion }] : []),
    { label: "Người gửi", value: reporterName },
    { label: "Người xử lý", value: assigneeName },
    { label: "Ngày gửi", value: formatFeedbackDate(feedback.createdAt) },
    { label: "Cập nhật", value: formatFeedbackDate(feedback.updatedAt) },
  ];

  const canViewDiagnostics = canProcessFeedback(currentUser as any);
  const rawDiag = (feedback as any).diagnostics;
  const hasFullDiagnostics =
    canViewDiagnostics &&
    rawDiag &&
    typeof rawDiag === "object" &&
    !rawDiag.attached &&
    (Array.isArray(rawDiag.console) ||
      Array.isArray(rawDiag.failedRequests) ||
      Array.isArray(rawDiag.breadcrumbs) ||
      Array.isArray(rawDiag.unhandledErrors) ||
      Boolean(rawDiag.serverError) ||
      Boolean(rawDiag.env));

  const handleCopyDiagnostics = async () => {
    if (!rawDiag) return;
    try {
      const payload = {
        code: feedback.code,
        title: feedback.title,
        source: (feedback as any).source,
        fingerprint: (feedback as any).fingerprint,
        occurrenceCount: (feedback as any).occurrenceCount,
        affectedUsers: (feedback as any).affectedUsers,
        pageUrl: feedback.pageUrl,
        module: feedback.module,
        diagnostics: rawDiag,
      };
      await navigator.clipboard.writeText(JSON.stringify(payload, null, 2));
      setCopiedDiag(true);
      toast.success("Đã sao chép thông tin chẩn đoán cho dev/agent");
      setTimeout(() => setCopiedDiag(false), 2000);
    } catch {
      toast.error("Không thể sao chép vào clipboard");
    }
  };

  return (
    <div
      data-testid="fb-detail-page"
      className={cn(
        isModal ? "w-full px-4 pt-4 md:px-6 md:pt-5 min-w-0" : "mx-auto w-full max-w-6xl px-4 py-4 md:px-6 md:py-6 min-w-0",
        isModal ? "pb-4 md:pb-6" : hasActions ? "pb-28 md:pb-6" : "pb-6",
      )}
    >
      {/* Header */}
      <header className="mb-4 space-y-2">
        <div className="flex items-center gap-2">
          {!isModal && (
            <button
              type="button"
              onClick={() => navigate({ to: "/feedback" })}
              aria-label="Quay lại danh sách"
              className="h-11 w-11 -ml-2 flex items-center justify-center rounded-lg hover:bg-slate-100 text-slate-600 shrink-0"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
          )}
          <span data-testid="fb-detail-code" className="text-sm font-semibold text-slate-500">#{feedback.code}</span>
          {canEditFeedback && (
            <button
              type="button"
              data-testid="btn-edit-feedback"
              onClick={() => setEditDialogOpen(true)}
              className="ml-auto min-h-11 sm:min-h-9 px-3 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 text-sm font-semibold text-slate-700 flex items-center gap-1.5"
            >
              <Pencil className="h-4 w-4 text-blue-600" /> Sửa
            </button>
          )}
          {isModal && onClose && (
            <button
              type="button"
              data-testid="fb-detail-close"
              onClick={onClose}
              aria-label="Đóng"
              className={cn(
                "h-11 w-11 sm:h-9 sm:w-9 -mr-2 flex items-center justify-center rounded-lg hover:bg-slate-100 text-slate-500 shrink-0",
                !canEditFeedback && "ml-auto",
              )}
            >
              <X className="h-5 w-5" />
            </button>
          )}
        </div>
        <h1 data-testid="fb-detail-title" className="text-lg md:text-xl font-bold text-slate-900 break-words">
          {feedback.title}
        </h1>
        <div className="flex flex-wrap items-center gap-2">
          <FeedbackStatusChip status={feedback.status} />
          <FeedbackPriorityChip priority={feedback.priority} />
          <FeedbackTypeChip type={feedback.type} />
          <FeedbackSourceChip source={(feedback as any).source} />
          <FeedbackOccurrenceChip
            occurrenceCount={(feedback as any).occurrenceCount}
            affectedUsers={(feedback as any).affectedUsers}
            source={(feedback as any).source}
          />
        </div>
        <p className="text-sm text-slate-500">
          {reporterName} • {formatFeedbackDate(feedback.createdAt)}
        </p>
      </header>

      <div className="lg:grid lg:grid-cols-3 lg:gap-6">
        <div className="lg:col-span-2 space-y-4 min-w-0">
          {/* Mô tả */}
          <section className="bg-white rounded-xl border border-slate-200 shadow-sm">
            <h3 className="px-4 sm:px-5 py-3 border-b border-slate-100 text-base font-bold text-slate-800">Mô tả</h3>
            <FeedbackRichText
              data-testid="fb-detail-description"
              text={feedback.description}
              fallback="Không có mô tả."
              className="p-4 sm:p-5 text-slate-700"
            />
          </section>

          {/* Ảnh trước */}
          <section className="bg-white rounded-xl border border-slate-200 shadow-sm">
            <div className="px-4 sm:px-5 py-3 border-b border-slate-100 flex items-center gap-2">
              <h3 className="text-base font-bold text-slate-800">
                Ảnh trước ({beforeImages.length}/{FEEDBACK_MAX_IMAGES})
              </h3>
              {isUploadingBefore && <Loader2 className="h-4 w-4 animate-spin text-blue-500" />}
            </div>
            <div className="p-4 sm:p-5 space-y-3">
              {beforeImages.length ? (
                <FeedbackThumbGrid
                  testId="fb-before-images"
                  images={beforeImages}
                  onZoom={setZoomImage}
                  onRemove={canEditFeedback ? handleRemoveBefore : undefined}
                />
              ) : (
                <p className="text-sm text-slate-500">Chưa có ảnh.</p>
              )}
              {canEditFeedback && beforeImages.length < FEEDBACK_MAX_IMAGES && (
                <FeedbackImagePickerButtons
                  testIdPrefix="fb-before"
                  onFiles={handleAddBefore}
                  isUploading={isUploadingBefore}
                  disabled={isUploadingBefore}
                  desktopLabel="Thêm ảnh"
                />
              )}
            </div>
          </section>

          {/* Kết quả xử lý */}
          {isResolvedOrClosed && (
            <FeedbackResolutionResult
              resolutionNote={feedback.devResponse}
              beforeImages={beforeImages}
              afterImages={afterImages}
              resolvedBy={
                lastResolveEvent?.user?.fullName ||
                lastResolveEvent?.user?.email ||
                (feedback as any).assignedToName ||
                feedback.assignedTo
              }
              resolvedAt={lastResolveEvent?.createdAt || null}
              onZoomImage={setZoomImage}
            />
          )}

          {/* Chuẩn v2 §13: Nhật ký sự kiện (chỉ quản trị/dev — cùng quyền xem chẩn đoán) */}
          {canViewDiagnostics && <FeedbackEventTimeline feedbackId={feedback.id} code={feedback.code} />}

          {/* Thông tin chẩn đoán kỹ thuật */}
          {rawDiag && (
            <section
              data-testid="fb-diagnostics-section"
              className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden"
            >
              {hasFullDiagnostics ? (
                <>
                  <div className="px-4 sm:px-5 py-3 border-b border-slate-100 flex items-center justify-between gap-2 bg-slate-50/60">
                    <button
                      type="button"
                      data-testid="fb-diagnostics-toggle"
                      onClick={() => setDiagExpanded((v) => !v)}
                      className="flex items-center gap-2 text-left font-bold text-slate-800 text-sm sm:text-base cursor-pointer"
                    >
                      <Terminal className="h-4 w-4 text-purple-600 shrink-0" />
                      <span>Thông tin chẩn đoán</span>
                      <span className="text-xs font-normal text-slate-500">
                        ({(rawDiag.console?.length || 0) + (rawDiag.unhandledErrors?.length || 0)} console ·{" "}
                        {rawDiag.failedRequests?.length || 0} API lỗi · {rawDiag.breadcrumbs?.length || 0} thao tác)
                      </span>
                      {diagExpanded ? (
                        <ChevronUp className="h-4 w-4 text-slate-500" />
                      ) : (
                        <ChevronDown className="h-4 w-4 text-slate-500" />
                      )}
                    </button>
                    <button
                      type="button"
                      data-testid="fb-copy-diagnostics"
                      onClick={handleCopyDiagnostics}
                      className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 text-xs font-semibold text-slate-700 cursor-pointer shrink-0"
                    >
                      {copiedDiag ? <Check className="h-3.5 w-3.5 text-emerald-600" /> : <Copy className="h-3.5 w-3.5" />}
                      <span>Sao chép cho dev/agent</span>
                    </button>
                  </div>
                  {diagExpanded && (
                    <div className="p-4 sm:p-5 space-y-3">
                      <div className="flex flex-wrap gap-1.5 border-b border-slate-100 pb-2">
                        {[
                          {
                            key: "console" as const,
                            label: `Console & Lỗi (${(rawDiag.console?.length || 0) + (rawDiag.unhandledErrors?.length || 0) + (rawDiag.serverError ? 1 : 0)})`,
                          },
                          {
                            key: "network" as const,
                            label: `API lỗi (${rawDiag.failedRequests?.length || 0})`,
                          },
                          {
                            key: "breadcrumbs" as const,
                            label: `Thao tác (${rawDiag.breadcrumbs?.length || 0})`,
                          },
                          {
                            key: "env" as const,
                            label: "Môi trường",
                          },
                        ].map((tab) => (
                          <button
                            key={tab.key}
                            type="button"
                            data-testid={`fb-diag-tab-${tab.key}`}
                            onClick={() => setDiagTab(tab.key)}
                            className={cn(
                              "px-2.5 py-1 rounded-md text-xs font-semibold cursor-pointer transition-colors",
                              diagTab === tab.key
                                ? "bg-slate-900 text-white"
                                : "bg-slate-100 text-slate-600 hover:bg-slate-200",
                            )}
                          >
                            {tab.label}
                          </button>
                        ))}
                      </div>

                      {diagTab === "console" && (
                        <div className="space-y-2 max-h-72 overflow-y-auto text-xs font-mono">
                          {rawDiag.serverError && (
                            <div className="p-2.5 rounded-lg bg-rose-50 border border-rose-200 text-rose-900 whitespace-pre-wrap break-all">
                              <div className="font-bold">[SERVER ERROR] {rawDiag.serverError.name}: {rawDiag.serverError.message}</div>
                              {rawDiag.serverError.stack && <div className="mt-1 text-[11px] text-rose-700">{rawDiag.serverError.stack}</div>}
                            </div>
                          )}
                          {(rawDiag.unhandledErrors || []).map((err: any, idx: number) => (
                            <div key={`unhandled-${idx}`} className="p-2.5 rounded-lg bg-rose-50 border border-rose-200 text-rose-900 whitespace-pre-wrap break-all">
                              <div className="font-bold">[{err.kind?.toUpperCase()}] {err.name}: {err.message}</div>
                              {err.stack && <div className="mt-1 text-[11px] text-rose-700">{err.stack}</div>}
                            </div>
                          ))}
                          {(rawDiag.console || []).map((entry: any, idx: number) => (
                            <div
                              key={`console-${idx}`}
                              className={cn(
                                "p-2 rounded border whitespace-pre-wrap break-all",
                                entry.level === "error"
                                  ? "bg-red-50/70 border-red-200 text-red-800"
                                  : "bg-amber-50/70 border-amber-200 text-amber-900",
                              )}
                            >
                              <span className="font-bold uppercase mr-1.5">[{entry.level}]</span>
                              <span>{entry.message}</span>
                              {entry.stack && <div className="mt-1 text-[11px] opacity-80">{entry.stack}</div>}
                            </div>
                          ))}
                          {!rawDiag.serverError &&
                            !(rawDiag.unhandledErrors?.length > 0) &&
                            !(rawDiag.console?.length > 0) && (
                              <p className="text-slate-400 font-sans">Không ghi nhận lỗi console.</p>
                            )}
                        </div>
                      )}

                      {diagTab === "network" && (
                        <div className="space-y-1.5 max-h-72 overflow-y-auto text-xs font-mono">
                          {(rawDiag.failedRequests || []).length > 0 ? (
                            (rawDiag.failedRequests || []).map((req: any, idx: number) => (
                              <div key={idx} className="p-2 rounded border border-slate-200 bg-slate-50 break-all">
                                <span className="font-bold text-rose-600">{req.method}</span>{" "}
                                <span className="text-slate-800">{req.url}</span>{" "}
                                <span className="px-1.5 py-0.5 rounded bg-rose-100 text-rose-700 font-bold">
                                  {req.status}
                                </span>{" "}
                                <span className="text-slate-500">{req.durationMs}ms</span>
                                {req.errorMessage && <div className="text-rose-700 mt-0.5">{req.errorMessage}</div>}
                              </div>
                            ))
                          ) : (
                            <p className="text-slate-400 font-sans">Không có API lỗi.</p>
                          )}
                        </div>
                      )}

                      {diagTab === "breadcrumbs" && (
                        <div className="space-y-1 max-h-72 overflow-y-auto text-xs font-mono">
                          {(rawDiag.breadcrumbs || []).length > 0 ? (
                            (rawDiag.breadcrumbs || []).map((b: any, idx: number) => (
                              <div key={idx} className="p-1.5 rounded border border-slate-100 bg-slate-50 flex items-center gap-2 break-all">
                                <span className="px-1.5 py-0.5 rounded bg-slate-200 text-slate-700 font-semibold uppercase text-[10px]">
                                  {b.type}
                                </span>
                                <span className="text-slate-800">{b.label || b.to || b.from}</span>
                              </div>
                            ))
                          ) : (
                            <p className="text-slate-400 font-sans">Không có lịch sử thao tác.</p>
                          )}
                        </div>
                      )}

                      {diagTab === "env" && (
                        <pre className="p-3 rounded-lg bg-slate-900 text-slate-100 text-xs font-mono overflow-x-auto">
                          {JSON.stringify(rawDiag.env || {}, null, 2)}
                        </pre>
                      )}
                    </div>
                  )}
                </>
              ) : (
                <div className="px-4 sm:px-5 py-3 flex items-center gap-2 text-sm text-slate-600 bg-slate-50/60">
                  <Terminal className="h-4 w-4 text-slate-500 shrink-0" />
                  <span>Đã đính kèm thông tin chẩn đoán kỹ thuật</span>
                </div>
              )}
            </section>
          )}

          {/* Lịch sử & bình luận */}
          <FeedbackCommentsTimeline
            feedbackId={feedback.id}
            comments={comments}
            onImageZoom={setZoomImage}
            composerStickyClassName={
              isModal
                ? undefined
                : "max-md:sticky max-md:bottom-[calc(76px+env(safe-area-inset-bottom))] max-md:shadow-[0_-4px_12px_rgba(0,0,0,0.06)]"
            }
          />
        </div>

        {/* Cột phải (desktop ≥1024): thông tin phiếu + hành động */}
        <aside className="mt-4 lg:mt-0 space-y-4 min-w-0">
          {hasActions && (
            <section className="hidden md:block bg-white rounded-xl border border-slate-200 shadow-sm p-4 space-y-2">
              <h3 className="text-sm font-bold text-slate-700 mb-1">Hành động</h3>
              <div className="flex flex-col gap-2" data-testid="fb-actions-desktop">{actionButtons("stack")}</div>
            </section>
          )}
          <section className="bg-white rounded-xl border border-slate-200 shadow-sm">
            <h3 className="px-4 py-3 border-b border-slate-100 text-base font-bold text-slate-800">Thông tin phiếu</h3>
            <dl className="divide-y divide-slate-100">
              {infoRows.map((row) => (
                <div key={row.label} className="px-4 py-2.5 flex items-start justify-between gap-3 text-sm">
                  <dt className="text-slate-500 shrink-0">{row.label}</dt>
                  <dd className="text-slate-800 text-right min-w-0 break-words">{row.value}</dd>
                </div>
              ))}
            </dl>
          </section>
        </aside>
      </div>

      {/* Thanh hành động dính đáy trên mobile (< 768px) */}
      {hasActions && (
        <div
          data-testid="fb-actions-mobile"
          className={cn(
            "md:hidden bg-white/95 backdrop-blur border-t border-slate-200 px-4 pt-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] flex gap-2",
            // Trong popup: dính đáy popup; trang riêng: dính đáy màn hình
            isModal ? "sticky bottom-0 z-10 -mx-4 -mb-4 mt-4" : "fixed inset-x-0 bottom-0 z-[55]",
          )}
        >
          {actionButtons("bar")}
        </div>
      )}

      <FeedbackStateTransitionDialog
        open={transitionDialogOpen}
        onOpenChange={setTransitionDialogOpen}
        oldStatus={(feedback.status as FeedbackStatus) || null}
        newStatus={targetStatus}
        feedbackType={feedback.type}
        onConfirm={handleConfirmTransition}
        isSubmitting={updateStatusMutation.isPending}
      />

      <CreateFeedbackDialog open={editDialogOpen} onOpenChange={setEditDialogOpen} initialData={feedback} />

      <FeedbackImageLightbox url={zoomImage} onClose={() => setZoomImage(null)} />
    </div>
  );
}
